import { type Ref, computed, ref, unref, useAsync, useContext } from "@nuxtjs/composition-api";
import { type AxiosError } from "axios";
import { storeToRefs } from "pinia";

import { useCategoriesStore } from "@/store";
import { handleServiceError } from "@/utils/api";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useCategory
 *
 * @desc     The composable takes in a cat/hashtag name string param.
 *           An internal check toggles functionality for fetching images and category data.
 *           All category usage should run through this composable to standardize usage.
 *           We return name, description, hashtag, and images for the category/hashtag.
 *
 * @summary  A composable retrieves a category/hashtag by name from the store
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @param {string} categoryName - hashtag or cat name
 *
 * @returns {ICategory} Object used for category display and naming
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @example <caption>Usage of `useCategory` for category pages</caption>
 * ```ts
 * const { displayName, image, imageSrcset } = useCategory(toRef(props, "categoryName"));
 * ```
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

export const useCategory = (categoryName: Ref<string> | string) => {
  const catStore = useCategoriesStore();
  const catName = ref(unref(categoryName));

  const { fetchState } = useCategories();
  const hasHashtagFeed = computed(
    () => catStore.category(catName.value)?.actions[0].actionType === "v1_hashtag" || catName.value === "fashionmonth",
  );

  const hashtag = !catName.value.startsWith("ltk") && !hasHashtagFeed.value ? "ltk" + catName.value : catName.value;
  const name = !catName.value.startsWith("ltk") && !hasHashtagFeed.value ? "ltk" + catName.value : catName.value;
  const description = computed(() => catStore.category(catName.value)?.subtitle);
  const displayName = computed(() => catStore.category(catName.value)?.title);
  const image = computed(() => catStore.category(catName.value)?.url ?? "");

  return {
    name,
    displayName,
    description,
    hashtag,
    image,
    fetchState,
  };
};

export const useCategories = () => {
  const catStore = useCategoriesStore();
  const pending = ref(true);

  const fetch = async () => {
    const ctx = useContext();
    // Skip server fetching, because data should already be loaded in async
    // But using fetchOnServer: false will render a spinner instead
    if (process.server || catStore.categoriesList.length) {
      return;
    }

    try {
      await catStore.getHashtags();
    } catch (err) {
      return handleServiceError({
        context: ctx,
        error: {
          status: (err as AxiosError).response?.status ?? 0,
          errorCode: "ltk-categories-not-found",
          developerMessage: `Error fetching categories`,
        },
        originalError: err,
      });
    }
  };

  useAsync(async () => {
    await fetch();
    pending.value = false;
  }, "ltkCategoriesFetch");

  return {
    fetchState: { pending },
    ...storeToRefs(catStore),
  };
};
